/**
 * useColumnSizing
 *
 * Persists TanStack Table column sizing in localStorage under
 * `dt:${tableId}:widths`. Writes are debounced (200ms).
 *
 * @module components/common/DataTable/useColumnSizing
 */

import { useState, useEffect, useRef, useCallback } from 'react';

const STORAGE_PREFIX = 'dt:';
const STORAGE_SUFFIX = ':widths';
const DEBOUNCE_MS = 200;

const storageKey = (tableId) => `${STORAGE_PREFIX}${tableId}${STORAGE_SUFFIX}`;

const readInitial = (tableId) => {
  if (!tableId || typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(storageKey(tableId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Wraps TanStack column sizing state with persistence.
 *
 * @param {string} tableId - Unique id used as localStorage namespace
 * @returns {[Object, Function]} [columnSizing, setColumnSizing]
 */
const useColumnSizing = (tableId) => {
  const [columnSizing, setColumnSizingState] = useState(() => readInitial(tableId));
  const timerRef = useRef(null);

  // Debounced write to localStorage
  useEffect(() => {
    if (!tableId || typeof window === 'undefined') return undefined;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(storageKey(tableId), JSON.stringify(columnSizing));
      } catch {
        // Quota / private mode — silently ignore
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [tableId, columnSizing]);

  const setColumnSizing = useCallback((updater) => {
    setColumnSizingState((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  return [columnSizing, setColumnSizing];
};

export default useColumnSizing;
